'use client';

import React, { useEffect, useRef } from 'react';

import Autoplay from 'embla-carousel-autoplay';

import Image from 'next/image';

import { Card, CardContent } from '@/components/ui/card';
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { cn } from '@/lib/utils';

export type CarouselBlockProps = {
    items: {
        image: { url: string; alt: string; width: number; height: number };
        caption?: string;
    }[];
    className?: string;
};

export const VideoBanner = ({ src, play }: { src: string; play: boolean }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const playVideo = () => {
            videoRef.current?.play();
        };

        const pauseVideo = () => {
            videoRef.current?.pause();
        };

        if (play) {
            playVideo();
        } else {
            pauseVideo();
        }
    }, [play, videoRef]);

    return (
        <video
            ref={videoRef}
            src={src}
            title="video"
            muted
            loop
            autoPlay
            className="absolute inset-0 h-full w-full object-fill object-center m-0!"
        />
    );
};

export const ImageBanner = ({
    src,
    alt,
    width,
    height,
}: {
    src: string;
    alt: string;
    width: number;
    height: number;
}) => {
    return (
        <Image
            src={src}
            alt={alt}
            width={width}
            height={height}
            className="absolute inset-0 h-full w-full object-fill object-center m-0!"
            unoptimized
        />
    );
};

export const CarouselBlock: React.FC<CarouselBlockProps> = ({ items, className }) => {
    const [api, setApi] = React.useState<CarouselApi>();
    const [current, setCurrent] = React.useState(0);
    const [count, setCount] = React.useState(0);

    React.useEffect(() => {
        if (!api) {
            return;
        }

        setCount(api.scrollSnapList().length);
        setCurrent(api.selectedScrollSnap() + 1);

        api.on('select', () => {
            setCurrent(api.selectedScrollSnap() + 1);
        });
    }, [api]);

    if (!items?.length) return null;

    return (
        <section className={cn('container mx-auto my-8 w-full', className)}>
            <div className="relative w-full">
                <Carousel
                    setApi={setApi}
                    opts={{
                        align: 'start',
                        loop: true,
                    }}
                    plugins={[
                        Autoplay({
                            delay: 8_000,
                            stopOnFocusIn: true,
                            stopOnInteraction: true,
                        }),
                    ]}
                    className="w-full"
                >
                    <CarouselContent>
                        {items.map((item, index) => (
                            <CarouselItem key={index}>
                                <Card className="m-px w-full overflow-hidden rounded-2xl bg-[#000000] py-0!">
                                    <CardContent className="relative flex lg:h-[900px] aspect-[16/9] items-center justify-center p-0! m-0!">
                                        {item.image.url.endsWith('.mp4') ? (
                                            <VideoBanner src={item.image.url} play={current === index + 1} />
                                        ) : (
                                            <ImageBanner
                                                src={item.image.url}
                                                alt={item.image.alt}
                                                width={item.image.width}
                                                height={item.image.height}
                                            />
                                        )}
                                    </CardContent>
                                </Card>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 space-x-3 hidden md:flex">
                    {Array.from({ length: count }).map((_, index) => (
                        <button
                            key={index}
                            onClick={() => api?.scrollTo(index)}
                            type="button"
                            aria-label={`Go to slide ${index + 1}`}
                            className={cn(
                                'cursor-pointer rounded-full bg-white',
                                'transition-[opacity,size] duration-500 ease-in-out',
                                {
                                    'size-[12px] opacity-100': current === index + 1,
                                    'size-[10px] opacity-50': current !== index + 1,
                                }
                            )}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};
